import React, { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "@/App";
import { useLocation } from "react-router-dom";
import { Shield, FileText, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// localStorage key: scoped by both user id AND policy version so a policy
// revision automatically invalidates a previous acceptance cache. Format:
//   ge.terms_accepted.{userId}.{version} = "1"
const lsKey = (userId, version) => `ge.terms_accepted.${userId}.${version || "_"}`;

export default function TermsAcceptanceDialog() {
  const { user, checkAuth } = useContext(AuthContext);
  const location = useLocation();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // current_terms_version is surfaced by /auth/me. When it changes, the
  // dialog reappears even for previously-accepted users — exactly the
  // behaviour GDPR/CCPA auditors expect from a versioned consent flow.
  const currentVersion = user?.current_terms_version || null;
  const userVersion = user?.terms_version || null;

  // Local sticky-dismissed flag — once accepted in this session, the dialog
  // stays hidden regardless of any context-state propagation race.
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined" || !user?.id) return false;
    try {
      return localStorage.getItem(lsKey(user.id, currentVersion)) === "1";
    } catch {
      return false;
    }
  });
  const lastSyncedKey = useRef(null);

  // Whenever the user object changes (login / checkAuth result), sync local
  // dismissed flag with server-authoritative state. We trust the server:
  //   userVersion === currentVersion → already accepted current version, hide.
  //   userVersion !== currentVersion → policy has changed (or never accepted), show.
  useEffect(() => {
    if (!user?.id) return;
    const key = lsKey(user.id, currentVersion);
    if (lastSyncedKey.current === key) return;
    lastSyncedKey.current = key;
    const serverAccepted = !!userVersion && userVersion === currentVersion;
    if (serverAccepted) {
      try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
      setDismissed(true);
    } else {
      // Either fresh user or policy bumped — clear any stale "1" for this
      // version key and reset local dismissed state so the dialog reappears.
      try { localStorage.removeItem(key); } catch { /* ignore */ }
      setDismissed(false);
      setAccepted(false);
    }
  }, [user, currentVersion, userVersion]);

  // Don't show on legal pages (so users can actually read them) or public pages
  const isLegalPage = location.pathname.startsWith("/legal");
  // Re-evaluate visibility on every render: a server-confirmed acceptance of
  // the CURRENT version is the strongest signal; local dismissed handles the
  // immediate post-click frame before the context refresh lands.
  const serverAcceptedCurrent = !!userVersion && userVersion === currentVersion;
  if (!user || isLegalPage) return null;
  if (serverAcceptedCurrent || dismissed) return null;

  const handleAccept = async () => {
    if (!accepted) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/accept-terms`, {}, { withCredentials: true });
      // Persist acceptance LOCALLY first — guarantees the dialog hides
      // instantly on this device, independent of the next checkAuth.
      try { localStorage.setItem(lsKey(user.id, currentVersion), "1"); } catch { /* ignore */ }
      setDismissed(true);
      toast.success("Thanks — you're all set.");
      // Background refresh to keep server state in sync; not awaited.
      checkAuth().catch(() => { /* non-fatal */ });
    } catch {
      toast.error("Failed to record acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Returning user re-prompted after a policy bump gets slightly different
  // copy so they're not confused about why they're seeing this again.
  const isRevision = !!userVersion && userVersion !== currentVersion;

  return (
    <div
      data-testid="terms-acceptance-overlay"
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-ink px-6 py-4">
          <h2 className="text-white text-lg font-bold">
            {isRevision ? "We've updated our policies" : "Before you continue"}
          </h2>
          <p className="text-accent-tint text-sm mt-1">
            {isRevision
              ? "Please review the new Privacy Policy and Terms of Use to keep using Golf Eclectic."
              : "Please review and accept our policies to use Golf Eclectic."}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-ink-muted leading-relaxed">
            {isRevision
              ? "Our Privacy Policy and Terms have been updated. Please confirm you've reviewed the latest versions to continue."
              : "To continue, please confirm you've read and agree to our Privacy Policy and Terms of Use."}
          </p>

          {/* Policy Links */}
          <div className="space-y-2">
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg border border-line hover:border-accent hover:bg-accent-tint-2 transition-colors"
            >
              <Shield className="w-5 h-5 text-accent flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-ink">Privacy Policy</p>
                <p className="text-xs text-ink-soft">How we collect, use, and protect your data</p>
              </div>
            </a>
            <a
              href="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg border border-line hover:border-accent hover:bg-accent-tint-2 transition-colors"
            >
              <FileText className="w-5 h-5 text-accent flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-ink">Terms of Use</p>
                <p className="text-xs text-ink-soft">Rules for using the service, fair play, and liability</p>
              </div>
            </a>
          </div>

          {/* Checkbox */}
          <button
            data-testid="terms-checkbox"
            onClick={() => setAccepted(!accepted)}
            className="flex items-start gap-3 w-full text-left p-3 rounded-lg hover:bg-app transition-colors"
            aria-pressed={accepted}
          >
            {accepted ? (
              <CheckSquare className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
            ) : (
              <Square className="w-5 h-5 text-ink-soft flex-shrink-0 mt-0.5" />
            )}
            <span className="text-sm text-ink leading-relaxed">
              I have read and agree to the <strong>Privacy Policy</strong> and <strong>Terms of Use</strong>
            </span>
          </button>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-app border-t border-line">
          <Button
            data-testid="accept-terms-button"
            onClick={handleAccept}
            disabled={!accepted || submitting}
            className="w-full bg-ink hover:bg-ink-hover disabled:bg-line-strong"
          >
            {submitting ? "Processing..." : isRevision ? "Accept updated policies" : "Accept & Continue"}
          </Button>
          {currentVersion && (
            <p className="text-[10px] text-ink-soft/60 font-mono text-center mt-2" data-testid="terms-version-tag">
              policy {currentVersion}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
